import { StrictMode, lazy } from 'react';

import type { UserContextConfig } from '@coralogix/browser';
import { CoralogixRum } from '@coralogix/browser';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import ReactDOM from 'react-dom/client';

import { version } from '../package.json';

import type { CleanGeoData, RawGeoData } from './features/geography/types';
import { initI18Next } from './i18n/config';
import { analytics } from './services/analytics';
import { auth, type Profile } from './services/auth';
import { handleAffiliateUTM, sendGoogleTagUTMSource } from './services/googleTagEvent';
import { intercom } from './services/intercom';
import { createLDContext, getBootstrapFlags } from './services/launchDarkly';
import { fetchAnonymousBootstrapFlags } from './services/launchDarklyApi';
import { lifecycleEventEmitterService } from './services/lifecycleEventEmitter/LifecycleEventEmitterService';
import { OneSignalService } from './services/oneSignal.service';
import { OptimoveService } from './services/optimove.service';
import { warmupPaycomScript } from './services/paycomPreload';
import { platformContextService } from './services/platformContext/PlatformContextService';
import { liveGeoStore } from './stores/live-geo/LiveGeoStore';
import { isProduction } from './utils/getEnvironment';
import { isCmsPages } from './utils/isExemptPage';
import { devError } from './utils/log';

const AppError = lazy(() => import('./pages/errors/AppError'));

export async function render(component: React.ComponentType) {
  platformContextService.initFromURL();
  lifecycleEventEmitterService.emitMessage('ON_APP_STARTED');
  const el = document.getElementById('root');

  if (!el) {
    throw new Error('Root element not found');
  }

  handleAffiliateUTM();
  sendGoogleTagUTMSource();

  const root = ReactDOM.createRoot(el);

  let ComponentToRender = component;
  let profile: Profile | null = null;

  if (!isProduction) {
    try {
      CoralogixRum.init({
        public_key: import.meta.env.VITE_CORALOGIX_PUBLIC_KEY,
        application: 'player-frontend',
        version,
        coralogixDomain: 'US2',
        traceParentInHeader: {
          enabled: true,
          // options: {
          //   // Only propagate trace headers to first-party APIs to avoid
          //   // CORS preflights and breaking third-party requests.
          //   propagateTraceHeaderCorsUrls: [new RegExp('.*')], // or specify your backend domain
          // },
        },
        sessionRecordingConfig: {
          enable: true,
          autoStartSessionRecording: true,
          // Disabled to avoid shipping potentially sensitive console payloads
          // (auth tokens, custom-token responses) to a third party.
          recordConsoleEvents: false,
          sessionRecordingSampleRate: 100,
        },
      });
    } catch (error) {
      devError('Failed to initialize Coralogix RUM', error);
    }
  }

  try {
    if (window.newrelic) {
      window.newrelic.setApplicationVersion(version);
    }

    // We don't call login logic for cms pages, we don't want to limit access by geoblock to pages such as privacy policy, terms of use, etc...
    if (!isCmsPages()) {
      profile = await auth.initAuth();
    }
  } catch (e: any) {
    devError('Error initializing app', e);

    if (e.response?.status !== 403) {
      ComponentToRender = function AppErrorWrapper() {
        return <AppError error={e as Error} />;
      };
    }
  } finally {
    console.log(`Version ${version} 🎉`);

    let currentGeo: { clean: CleanGeoData | null; raw: RawGeoData | null } | null = null;
    let anonymousBootstrapFlags: Record<string, any> | null = null;
    if (!profile) {
      [currentGeo, anonymousBootstrapFlags] = await Promise.all([
        liveGeoStore.getCurrentGeoData(),
        fetchAnonymousBootstrapFlags(),
      ]);
    }

    void initI18Next(currentGeo?.clean?.defaultLanguage);

    const bootstrapFlags = getBootstrapFlags(profile?.ldBootstrapFlags, anonymousBootstrapFlags);
    const userLdContext = createLDContext(profile, currentGeo?.raw);

    const initDeferredServices = () => {
      void OneSignalService.init();
      void OptimoveService.init();
      analytics.init();
      intercom.init(profile);
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(initDeferredServices);
    } else {
      // Safari doesn't support requestIdleCallback — wait for the first
      // frame to paint, then yield so the paint completes before we init.
      requestAnimationFrame(() => setTimeout(initDeferredServices, 0));
    }

    window.addEventListener('beforeunload', function userLeaving() {
      void analytics.sendEvent('app_close', {} as never);
    });

    if (profile) {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => warmupPaycomScript());
      } else {
        setTimeout(() => warmupPaycomScript(), 0);
      }
    }

    if (!isProduction && profile) {
      const userContext: UserContextConfig = {
        ...userLdContext,
        user_id: profile.firebaseUid,
        user_email: profile.email,
        user_name: profile.email,
      };

      CoralogixRum.setUserContext(userContext);
    }

    // https://docs.launchdarkly.com/sdk/client-side/react/react-web/#configuring-the-react-sdk
    // https://docs.launchdarkly.com/sdk/features/bootstrapping
    const LDProvider = await asyncWithLDProvider({
      clientSideID: import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID,
      context: userLdContext,
      timeout: bootstrapFlags ? 1 : 3,
      options: {
        fetchGoals: false,
        flushInterval: 60000, // one minute (default is 2 sec)
        bootstrap: bootstrapFlags, // If undefined, LD will fetch flags normally (graceful fallback)
      },
      reactOptions: {
        useCamelCaseFlagKeys: false,
      },
    });
    root.render(
      <StrictMode>
        <LDProvider>
          <ComponentToRender />
        </LDProvider>
      </StrictMode>,
    );
  }
}
